import { computed, ref, type MaybeRefOrGetter, toValue } from 'vue'
import {
  isEmptyMandatoryValue,
  isFieldMandatory,
} from '@/features/Inventory/PassportRuntimeV2/model/validateMandatoryFields'
import { useTranslate } from '@/shared/lib'

export type MandatoryFieldErrorProps = {
  showMandatoryError?: boolean
  mandatory?: unknown
  type: string
}

/**
 * Mandatory empty-field highlight: only for required fields, after blur (touched) or failed save.
 */
export function useMandatoryFieldError(
  props: MaybeRefOrGetter<MandatoryFieldErrorProps>,
  value: MaybeRefOrGetter<unknown>,
) {
  const { trs } = useTranslate()
  const touched = ref(false)
  /** Ignore programmatic blur / menu-close / picker-close until the user interacts with the control. */
  const userInteracted = ref(false)

  function registerUserInteraction() {
    userInteracted.value = true
  }

  function markTouched() {
    if (!userInteracted.value) return
    touched.value = true
  }

  const visible = computed(() => {
    const p = toValue(props)
    if (!isFieldMandatory(p.mandatory)) return false
    // After failed save parent sets `showMandatoryError` only for fields that failed validation
    // (store may be empty while number inputs still display 0 as a visual default).
    if (Boolean(p.showMandatoryError)) return true
    if (!isEmptyMandatoryValue(p.type, toValue(value))) return false
    return touched.value
  })

  const message = computed(() => (visible.value ? trs('validate.required') : undefined))

  return { visible, message, markTouched, registerUserInteraction, touched }
}
